import './front-card-design-tool.system-administrator.screen.scss';
import {useParams} from 'react-router-dom';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {useContext, useEffect, useRef, useState} from 'react';
import {Entity} from '../../../../../../../backend/interfaces/entity';
import {
  Avatar,
  Box,
  Step,
  StepLabel,
  Stepper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {translations} from '../../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import BusinessIcon from '@mui/icons-material/Business';
import {UsersRepository} from '../../../../../../../repositories/users';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import PersonIcon from '@mui/icons-material/Person';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {User} from '../../../../../../../backend/interfaces/user';
import {LoadingContent} from '../../../../../../../components/loading-content/loading-content';
import ClearIcon from '@mui/icons-material/Clear';
import {ImagePaletteColors} from '../../../../../../../components/image-palette-colors/image-palette-colors';

// Constants
const cardsPerPage = 10;
const cardsSheetWidth = 2480;
const cardsSheetHeight = 3508;
const cardsPerLine = 2;
const initialPositionX = 204;
const initialPositionY = 100;
const cardWidth = 1003;
const cardHeight = 652;
const spacingLeft = 70;
const spacingDown = 15;
const textPositionX = 699;
const logoInitialPositionX = 20;
const logoInitialPositionY = 470;
const logoMaxHeight = 160;
const logoMaxWidth = 640;
const photoWidth = 382;
const photoHeight = 379;
const photoPositionX = 20;
const photoPositionY = 29;

// Functions
function convertArrayToIndex<T>(
  array: Array<any>,
  keyName: string
): {
  [key: string]: T;
} {
  // Init
  const result: {
    [key: string]: T;
  } = {};

  // For each item
  for (const item of array) {
    // Get key
    const _key = item[keyName.toString()];

    // Quick check
    if (!_key) {
      continue;
    }

    // Get key
    const key = _key.toString();

    // If key was not added yet
    if (!result[key]) {
      // Add it now
      result[key] = item;
    }
  }

  // Done
  return result;
}

// Interface
interface CardsSheet {
  number: number;
  usersIds: Array<string>;
  usersIndex: {[key: string]: User};
  entityPicture: string | undefined;
  entityNameToUse: string | undefined;
  entityNameTextColor: string;
}

// Component
const CardSheetComponent = (props: {sheet: CardsSheet}) => {
  // Hold Ref to canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Aux functions
  const createLogoInfo = (logo: HTMLImageElement, positionX: number, positionY: number) => {
    // Hold vars
    let logoWidth, logoHeigth, logoPositionY;
    const logoPositionX = positionX + logoInitialPositionX;

    // Calculate logo size
    logoWidth = (logo.width * logoMaxHeight) / logo.height;

    // Check if logo is too big
    if (logoWidth > logoMaxWidth) {
      logoHeigth = (logo.height * logoMaxWidth) / logo.width;
      logoWidth = logoMaxWidth;
      const verticalSpacing = (logoMaxHeight - logoHeigth) / 2;
      logoPositionY = positionY + logoInitialPositionY + verticalSpacing;
    } else {
      logoHeigth = logoMaxHeight;
      logoPositionY = positionY + logoInitialPositionY;
    }

    // Return logo info
    return {
      width: logoWidth,
      height: logoHeigth,
      positionX: logoPositionX,
      positionY: logoPositionY,
    };
  };

  // Function to draw
  const drawCard = (
    context: CanvasRenderingContext2D,
    positionX: number,
    positionY: number,
    index: number,
    user: User
  ) => {
    // Count name words
    const nameToPrint = user.displayName ? user.displayName : user.name.split(' ')[0];
    const nameToPrintWords = nameToPrint.split(' ');

    // Init text coords
    let textLineHeight;

    // Draw Card
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = `${window.location.origin}/images/user-card-template-v1.png`;

    // On image load
    image.onload = () => {
      // Draw it
      context.drawImage(image, positionX, positionY, cardWidth, cardHeight);

      // #region User name

      // Set text style
      context.textAlign = 'center';
      context.fillStyle = '#FFFFFF';

      // Check number of words
      if (nameToPrintWords.length <= 2) {
        // Only 2 or less words
        context.font = 'bold 78pt Montserrat';
        textLineHeight = 108;
      } else {
        // More than 2 words
        context.font = 'bold 60pt Montserrat';
        textLineHeight = 90;
      }

      // Calculate Y coords
      const availableHeightToWrite = 430;
      const heightToWriteNeeded = nameToPrintWords.length * textLineHeight;
      const spaceAbove = (availableHeightToWrite - heightToWriteNeeded) / 2;
      const firstYAvailable = positionY + spaceAbove + textLineHeight;

      // Check each word size
      nameToPrintWords.forEach((word) => {
        // If it was more than 9 letters
        if (word.length > 9) {
          // Change font size
          context.font = 'bold 50pt Montserrat';
        } else if (word.length > 8) {
          // Change font size
          context.font = 'bold 55pt Montserrat';
        } else if (word.length > 7) {
          // Change font size
          context.font = 'bold 60pt Montserrat';
        }
      });

      // Write each word
      for (let i = 0; i < nameToPrintWords.length; i++) {
        // Draw line
        context.fillText(
          nameToPrintWords[i],
          positionX + textPositionX,
          firstYAvailable + i * textLineHeight
        );
      }

      // #endregion

      // #region Entity Picture

      // Check Entity Picture
      if (props.sheet.entityPicture) {
        // Define
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.src = props.sheet.entityPicture;

        // On image load
        logo.onload = () => {
          // Get Logo Info
          const logoInfo = createLogoInfo(logo, positionX, positionY);

          // Draw Logo
          context.drawImage(
            logo,
            logoInfo.positionX,
            logoInfo.positionY,
            logoInfo.width,
            logoInfo.height
          );

          // #region Entity Name

          // If defined
          if (props.sheet.entityNameToUse) {
            // Set text style
            const entityNameSpacing = 20;

            // context.fillStyle = istColor;
            context.fillStyle =
              props.sheet.entityNameTextColor !== '' ? props.sheet.entityNameTextColor : '#000000';
            context.font = '30pt Montserrat';
            context.textAlign = 'start';

            const entityNameLines = props.sheet.entityNameToUse.split('\n');
            const entityNameLineHeight = 45;

            const entityNamePositionX =
              positionX + logoInitialPositionX + logoInfo.width + entityNameSpacing;

            const availableHeightToWrite = logoInfo.height;
            const heightToWriteNeeded = entityNameLines.length * entityNameLineHeight;
            const spaceAbove = (availableHeightToWrite - heightToWriteNeeded) / 2;
            const firstYAvailable =
              positionY + logoInitialPositionY + spaceAbove + entityNameLineHeight - 8;

            for (let j = 0; j < entityNameLines.length; j++) {
              // Draw text
              context.fillText(
                entityNameLines[j],
                entityNamePositionX,
                firstYAvailable + entityNameLineHeight * j
              );
            }
          }

          // #endregion
        };
      }

      // #endregion

      // #region User Picture

      const photo = new Image();
      photo.crossOrigin = 'anonymous';

      if (user.picture) {
        photo.src = user.picture;
      } else {
        photo.src = `${window.location.origin}/images/user-picture-placeholder-v1.png`;
      }

      // Draw Photo
      photo.onload = () => {
        // get the scale
        // it is the min of the 2 ratios
        const scaleFactor = Math.min(photo.width / photoWidth, photo.height / photoHeight);

        // Crop it
        // https://stackoverflow.com/a/26015533
        const sx = (photo.width - photoWidth * scaleFactor) / 2;
        const sy = (photo.height - photoHeight * scaleFactor) / 2;
        context.drawImage(
          photo,
          sx,
          sy,
          photo.width - sx * 2,
          photo.height - sy * 2,
          positionX + photoPositionX,
          positionY + photoPositionY,
          photoWidth,
          photoHeight
        );
      };

      // #endregion
    };
  };

  const drawSheet = (canvas: HTMLCanvasElement) => {
    // Hold counts
    let cardCount = 0;
    let cardsPerColumm = 0;

    // Calculate columns
    if (props.sheet.usersIds.length % 2 === 0) {
      cardsPerColumm = props.sheet.usersIds.length / 2;
    } else {
      cardsPerColumm = 1 + (props.sheet.usersIds.length - 1) / 2;
    }

    // Get Canvas Context to Draw over it
    const context = canvas.getContext('2d');

    // Quick check
    if (!context) {
      return;
    }

    // Draw Background
    context.beginPath();
    context.rect(0, 0, cardsSheetWidth, cardsSheetHeight);
    context.fillStyle = '#FFFFFF';
    context.fill();

    // For each card per line
    for (var i = 0; i < cardsPerLine; i++) {
      // Calculate position X
      const positonX = initialPositionX + cardWidth * i + spacingLeft * i;

      // For each card per column
      for (var j = 0; j < cardsPerColumm; j++) {
        // Calculate position Y
        const positonY = initialPositionY + cardHeight * j + spacingDown * j;

        // Check if user exists
        if (props.sheet.usersIndex[props.sheet.usersIds[cardCount]]) {
          // Draw Card
          drawCard(
            context,
            positonX,
            positonY,
            cardCount,
            props.sheet.usersIndex[props.sheet.usersIds[cardCount]]
          );
        }

        // Increment card count to reach the next card
        cardCount++;
      }
    }
  };

  // On mount
  useEffect(() => {
    // Quick check
    if (canvasRef.current) {
      // Draw
      drawSheet(canvasRef.current);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw
  return (
    <canvas
      id={`cards-sheet-${props.sheet.number}-canvas`}
      className="cardSheet"
      ref={canvasRef}
      width={cardsSheetWidth}
      height={cardsSheetHeight}
    />
  );
};

export default function EntityFrontCardDesignToolSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Translations
  const {t} = useTranslation();

  // Get context
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold State
  const [activeStep, setActiveStep] = useState(0);

  // Hold Ref to canvas
  const customEntityPictureInput = useRef<HTMLInputElement>(null);

  // Form
  const [customEntityPicture, setCustomEntityPicture] = useState<
    | {
        file: File;
        url: string;
        width: number;
        height: number;
      }
    | undefined
  >(undefined);
  const [addEntityName, setAddEntityName] = useState<boolean>(false);
  const [entityNameToUse, setEntityNameToUse] = useState<string | undefined>(undefined);
  const [entityNameTextColor, setEntityNameTextColor] = useState<string>('');

  // Hold Users
  const [seniorUsers, setSeniorUsers] = useState<Array<AffiliatedUser> | null | undefined>(
    undefined
  );
  const [caregiverUsers, setCaregiverUsers] = useState<Array<AffiliatedUser> | null | undefined>(
    undefined
  );
  const [selectedSeniors, setSelectedSeniors] = useState<Array<string>>([]);
  const [selectedCaregivers, setSelectedCaregivers] = useState<Array<string>>([]);

  // Hold Cards
  const [cardsReadyToDraw, setCardsReadyToDraw] = useState<boolean>(false);
  const [cardsSheets, setCardsSheets] = useState<Array<CardsSheet>>([]);

  // Functions
  const loadUsers = async () => {
    // Init results
    const results: Array<AffiliatedUser> = [];
    let totalCount: number = -1;

    // While results are not completed
    while (results.length !== totalCount) {
      // Request
      const result: {totalCount: number; data: Array<AffiliatedUser>} =
        await UsersRepository.instance.getInEntity(id || '', 20, results.length);

      // Push results
      results.push(...result.data);

      // Update total count
      totalCount = result.totalCount;
    }

    // Segment
    const resultsSeniors: Array<AffiliatedUser> = [];
    const resultsCaregivers: Array<AffiliatedUser> = [];
    for (const result of results) {
      // Check
      if (result.user.type === UserType.Senior) {
        // Set
        resultsSeniors.push(result);
      } else if (result.user.type === UserType.Caregiver) {
        // Set
        resultsCaregivers.push(result);
      }
    }

    // Done
    setSeniorUsers(resultsSeniors);
    setCaregiverUsers(resultsCaregivers);
  };

  const seniorUserChecked = (userId: string | null, checked: boolean): void => {
    // Eval
    if (userId) {
      // Check
      if (checked) {
        setSelectedSeniors([...selectedSeniors, userId]);
      } else {
        setSelectedSeniors(selectedSeniors.filter((id) => id !== userId));
      }
    } else {
      // Check
      if (checked) {
        setSelectedSeniors(
          seniorUsers ? seniorUsers.map((affiliatedUser) => affiliatedUser.user.id ?? '') : []
        );
      } else {
        setSelectedSeniors([]);
      }
    }
  };

  const caregiverUserChecked = (userId: string | null, checked: boolean): void => {
    // Eval
    if (userId) {
      // Check
      if (checked) {
        setSelectedCaregivers([...selectedCaregivers, userId]);
      } else {
        setSelectedCaregivers(selectedCaregivers.filter((id) => id !== userId));
      }
    } else {
      // Check
      if (checked) {
        setSelectedCaregivers(
          caregiverUsers ? caregiverUsers.map((affiliatedUser) => affiliatedUser.user.id ?? '') : []
        );
      } else {
        setSelectedCaregivers([]);
      }
    }
  };

  const generateCards = async () => {
    // Quick check
    if (!entity) {
      return;
    }

    // Put Users Together
    const selectedUsersIds = [...selectedSeniors, ...selectedCaregivers];

    // Build all users
    const allUsers = [...(seniorUsers ?? []), ...(caregiverUsers ?? [])];

    // Build Index
    const usersIndex = convertArrayToIndex<User>(
      allUsers.map((affiliatedUser) => affiliatedUser.user),
      'id'
    );

    // Calculate number of sheets needed
    let numberOfSheets = 1;
    if (selectedUsersIds.length > cardsPerPage) {
      numberOfSheets = Math.ceil(selectedUsersIds.length / cardsPerPage);
    }

    // Init cards sheets
    const _cardsSheets: Array<CardsSheet> = [];

    // Evaluate
    if (numberOfSheets === 1) {
      // Single page with all users
      _cardsSheets.push({
        number: 1,
        usersIds: selectedUsersIds,
        usersIndex,
        entityPicture: customEntityPicture ? customEntityPicture.url : entity.picture,
        entityNameToUse,
        entityNameTextColor,
      });
    } else {
      // Multiple pages
      for (let i = 0; i < numberOfSheets; i++) {
        // Interval
        const start = cardsPerPage * i;
        const end = start + cardsPerPage;

        // Slice
        const pageSelectedUsersIds = selectedUsersIds.slice(start, end);

        // Push page
        _cardsSheets.push({
          number: i + 1,
          usersIds: pageSelectedUsersIds,
          usersIndex,
          entityPicture: customEntityPicture ? customEntityPicture.url : entity.picture,
          entityNameToUse,
          entityNameTextColor,
        });
      }
    }

    // Update UI
    setCardsSheets(_cardsSheets);
    setCardsReadyToDraw(true);
  };

  // On Page change
  useEffect(() => {
    // Check page
    if (activeStep === 0) {
      setCardsSheets([]);
    } else if (activeStep === 1) {
      // Get users
      loadUsers();
    } else if (activeStep === 2) {
      // Generate cards
      generateCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Init steps
  const steps = [
    t(translations.entityFrontCardDesignTool.step1),
    t(translations.entityFrontCardDesignTool.step2),
    t(translations.entityFrontCardDesignTool.step3),
  ];

  // Get current entity form
  const entity: Entity | undefined = officeContext.state.entityForm;
  if (!entity) return <>Entity Form not available</>;

  // Build
  return (
    <ContentPage>
      <h2>{t(translations.entityFrontCardDesignTool.title)}</h2>
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => {
          return (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box sx={{height: '16px'}}></Box>
      {activeStep === 0 && (
        <Box>
          <Box>
            <h3>{t(translations.entityFrontCardDesignTool.entityPicture)}</h3>
            {!customEntityPicture && (
              <Avatar
                sx={{
                  width: '120px',
                  height: '120px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#FFFFFF',
                  borderRadius: '16px',
                }}
                alt={entity.name}
                src={entity.picture ? entity.picture : ''}
              >
                <BusinessIcon
                  sx={{
                    fontSize: '100px',
                  }}
                />
              </Avatar>
            )}
            {customEntityPicture && (
              <>
                <img
                  id="custom-entity-picture"
                  className="custom-entity-picture"
                  alt="Entity Logo"
                  src={customEntityPicture.url}
                />
                <IconButton
                  aria-label="delete"
                  onClick={() => {
                    setCustomEntityPicture(undefined);
                    if (customEntityPictureInput && customEntityPictureInput.current) {
                      customEntityPictureInput.current.value = '';
                    }
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </>
            )}
            <br />
            <input
              ref={customEntityPictureInput}
              type="file"
              accept="image/*"
              onChange={(e) => {
                e.preventDefault();

                // Evaluate
                if (e.target && e.target.files) {
                  // Get file
                  const file = e.target.files[0];

                  // Read file when completes loading
                  const reader = new FileReader();
                  reader.onloadend = (evt) => {
                    // Create and Read Image Dimensions
                    var img = new Image();

                    // On load
                    img.onload = () => {
                      //Done
                      setCustomEntityPicture({
                        file: file,
                        url: reader.result as string,
                        width: img.width,
                        height: img.height,
                      });
                    };

                    // Set src
                    img.src = reader.result as string;
                  };

                  // If file exists
                  if (file != null) {
                    // Read file
                    reader.readAsDataURL(file);
                  }
                }
              }}
            />
          </Box>
          <Box sx={{paddingTop: '16px'}}>
            <h3>{t(translations.entityFrontCardDesignTool.entityName)}</h3>
            <p>{entity.name}</p>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addEntityName}
                    onChange={(event) => {
                      setAddEntityName(event.target.checked);
                      setEntityNameToUse(entity.name);
                    }}
                  />
                }
                label={t(translations.entityFrontCardDesignTool.addEntityName)}
              />
              {addEntityName && (
                <>
                  <TextField
                    sx={{
                      marginTop: '16px',
                    }}
                    id="add-entity-name"
                    label={t(translations.entityFrontCardDesignTool.entityNameToBePrinted)}
                    variant="outlined"
                    value={entityNameToUse}
                    multiline={true}
                    rows={4}
                    onChange={(event) => {
                      setEntityNameToUse(event.target.value);
                    }}
                  />
                  <ImagePaletteColors
                    src={customEntityPicture ? customEntityPicture.url : (entity.picture ?? '')}
                  >
                    {({data, loading, error}) => (
                      <>
                        {loading === false && data && (
                          <FormControl
                            sx={{
                              marginTop: '16px',
                            }}
                          >
                            <InputLabel id="entityNameTextColor-select-label">
                              {t(translations.entityFrontCardDesignTool.entityNameTextColor)}
                            </InputLabel>
                            <Select
                              labelId="entityNameTextColor-select-label"
                              id="entityNameTextColor-select"
                              value={entityNameTextColor}
                              label={t(translations.entityFrontCardDesignTool.entityNameTextColor)}
                              onChange={(event) => {
                                setEntityNameTextColor(event.target.value);
                              }}
                              endAdornment={
                                <InputAdornment position="end">
                                  <IconButton
                                    sx={{
                                      marginRight: '16px',
                                    }}
                                    onClick={() => {
                                      setEntityNameTextColor('');
                                    }}
                                    edge="end"
                                  >
                                    <ClearIcon />
                                  </IconButton>
                                </InputAdornment>
                              }
                            >
                              {Object.keys(data).map((key) => {
                                return (
                                  <MenuItem key={`color-${key}`} value={data[key]}>
                                    <div style={{display: 'flex'}}>
                                      <Box
                                        sx={{
                                          width: '24px',
                                          height: '24px',
                                          borderRadius: '50%',
                                          backgroundColor: data[key],
                                          marginRight: '16px',
                                        }}
                                      ></Box>
                                      <div>{key}</div>
                                    </div>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        )}
                      </>
                    )}
                  </ImagePaletteColors>
                </>
              )}
            </FormGroup>
          </Box>
          <Box sx={{paddingTop: '32px', textAlign: 'center'}}>
            <Button
              variant="contained"
              onClick={() => {
                setActiveStep(1);
              }}
            >
              {t(translations.entityFrontCardDesignTool.next)}
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 1 && (
        <Box>
          {seniorUsers === undefined && <LoadingContent />}
          {seniorUsers === null && (
            <Alert severity="error">
              {t(translations.entityFrontCardDesignTool.failedToLoadUsers)}
            </Alert>
          )}
          {seniorUsers && (
            <Box>
              <FormControlLabel
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                }}
                label={t(translations.office.system_administrator.tabs.seniors)}
                control={
                  <Checkbox
                    checked={seniorUsers.length === selectedSeniors.length}
                    indeterminate={
                      selectedSeniors.length !== 0 && seniorUsers.length !== selectedSeniors.length
                    }
                    onChange={(event) => {
                      seniorUserChecked(null, event.target.checked);
                    }}
                  />
                }
              />
              <Box sx={{display: 'flex', flexDirection: 'column', ml: 3}}>
                {seniorUsers.map((affiliatedUser, index) => {
                  return (
                    <FormControlLabel
                      key={`user-senior-${index}`}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: '30px',
                              height: '30px',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: '#FFFFFF',
                              borderRadius: '50%',
                              marginRight: '8px',
                            }}
                            alt={affiliatedUser.user.name}
                            src={affiliatedUser.user.picture ? affiliatedUser.user.picture : ''}
                          >
                            <PersonIcon
                              sx={{
                                fontSize: '20px',
                              }}
                            />
                          </Avatar>
                          <p>{affiliatedUser.user.name}</p>
                        </Box>
                      }
                      control={
                        <Checkbox
                          checked={selectedSeniors.includes(affiliatedUser.user.id ?? '')}
                          onChange={(event) => {
                            seniorUserChecked(affiliatedUser.user.id ?? '', event.target.checked);
                          }}
                        />
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          {caregiverUsers && (
            <Box sx={{paddingTop: '16px'}}>
              <FormControlLabel
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                }}
                label={t(translations.office.system_administrator.tabs.caregivers)}
                control={
                  <Checkbox
                    checked={caregiverUsers.length === selectedCaregivers.length}
                    indeterminate={
                      selectedCaregivers.length !== 0 &&
                      caregiverUsers.length !== selectedCaregivers.length
                    }
                    onChange={(event) => {
                      caregiverUserChecked(null, event.target.checked);
                    }}
                  />
                }
              />
              <Box sx={{display: 'flex', flexDirection: 'column', ml: 3}}>
                {caregiverUsers.map((affiliatedUser, index) => {
                  return (
                    <FormControlLabel
                      key={`user-caregiver-${index}`}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: '30px',
                              height: '30px',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: '#FFFFFF',
                              borderRadius: '50%',
                              marginRight: '8px',
                            }}
                            alt={affiliatedUser.user.name}
                            src={affiliatedUser.user.picture ? affiliatedUser.user.picture : ''}
                          >
                            <PersonIcon
                              sx={{
                                fontSize: '20px',
                              }}
                            />
                          </Avatar>
                          <p>{affiliatedUser.user.name}</p>
                        </Box>
                      }
                      control={
                        <Checkbox
                          checked={selectedCaregivers.includes(affiliatedUser.user.id ?? '')}
                          onChange={(event) => {
                            caregiverUserChecked(
                              affiliatedUser.user.id ?? '',
                              event.target.checked
                            );
                          }}
                        />
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          {(seniorUsers || caregiverUsers) && (
            <Box sx={{paddingTop: '32px', textAlign: 'center'}}>
              <Button
                variant="contained"
                onClick={() => {
                  setActiveStep(2);
                }}
              >
                {t(translations.entityFrontCardDesignTool.next)}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {activeStep === 2 && (
        <Box>
          {!cardsReadyToDraw && <LoadingContent />}
          {cardsReadyToDraw && (
            <>
              <Box sx={{}}>
                {cardsSheets.map((cardsSheet, index) => {
                  return (
                    <Box
                      key={`cards-sheet-${cardsSheet.number}-container`}
                      sx={{marginBottom: '16px', border: '1px solid red'}}
                    >
                      <CardSheetComponent sheet={cardsSheet} />
                    </Box>
                  );
                })}
              </Box>
              <Box
                sx={{
                  paddingTop: '32px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    if (cardsSheets) {
                      cardsSheets.forEach((cardsSheet) => {
                        // Generate canvas id
                        const canvasId = `cards-sheet-${cardsSheet.number}-canvas`;

                        // Generate download link
                        const downloadLink = document.createElement('a');

                        // Append download link to body
                        downloadLink.setAttribute('download', `${canvasId}.png`);

                        // Get canvas
                        const canvas: HTMLCanvasElement | null = document.getElementById(
                          canvasId
                        ) as HTMLCanvasElement | null;

                        // If canvas exists
                        if (canvas) {
                          // Generate blob
                          canvas.toBlob((blob) => {
                            // Check
                            if (blob) {
                              // Generate url
                              const url = URL.createObjectURL(blob);

                              // Set download link href
                              downloadLink.setAttribute('href', url);

                              // Click download link
                              downloadLink.click();
                            }
                          });
                        }
                      });
                    }
                  }}
                >
                  {t(translations.entityFrontCardDesignTool.save)}
                </Button>
                <Button
                  sx={{marginTop: '16px'}}
                  onClick={() => {
                    setActiveStep(0);
                  }}
                >
                  {t(translations.entityFrontCardDesignTool.restart)}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </ContentPage>
  );
}
